import { computed, watch } from 'vue'
import { useLogsStore } from '@/store/logs'

/**
 * Base class for syncables.
 * Syncables are classes that can be executed periodically to sync data from the backend.
 * They can have a list of preconditions that are defined as Computed Properties.
 * If the preconditions are not met, the syncable will not execute.
 */
class Syncable {
  /**
   * Creates a new Syncable instance.
   * @param globalUniqueIdentifier {string} The global unique identifier of the instance this syncable is for.
   * @param executionInterval {number} The interval in milliseconds at which the syncable should be executed.
   * @param preconditionBuilders {Array<Function>} An array of functions
   * that return computed properties representing preconditions.
   * The functions will be called with the global unique identifier as an argument.
   * @param numberOfUnsuccessfulExecutionsUntilBackoff {number} The number of unsuccessful
   * executions until the syncable will enable backoff logic.
   * @param backoffTime {number} The time in milliseconds the syncable will wait before trying to
   * execute again after its backoff logic was enabled.
   */
  constructor(
    globalUniqueIdentifier,
    executionInterval = 1000 * 60 * 10,
    preconditionBuilders = [],
    numberOfUnsuccessfulExecutionsUntilBackoff = 3,
    backoffTime = 1000 * 60 * 5
  ) {
    this.globalUniqueIdentifier = globalUniqueIdentifier
    this.handle = this.constructor.getSyncableHandle()
    this.executionInterval = executionInterval
    this.lastExecuted = null
    this.currentlyExecuting = false
    this.nextExecutionPromise = null
    this.nextExecutionResolve = null
    this.nextExecutionReject = null
    this.currentWorkloadPromise = null
    const preconditions = preconditionBuilders.map(builder => builder(globalUniqueIdentifier))
    this.numberOfUnsuccessfulExecutionsUntilBackoff = numberOfUnsuccessfulExecutionsUntilBackoff
    this.backoffTime = backoffTime

    this.currentUnsuccessfulExecutions = 0
    this.initialDataAvailable = computed(() => this.hasInitialData())
    this.satisfiesPreconditions = computed(() => {
      return preconditions.every(precondition => precondition.value)
    })

    this.attachWatchers()
  }

  /**
   * Marks the syncable as successfully executed.
   * Removes all Backoff logic so that the syncable can be executed normally again.
   */
  markSuccessfulExecution() {
    if (this.currentUnsuccessfulExecutions >= this.numberOfUnsuccessfulExecutionsUntilBackoff) {
      useLogsStore().addLogEntry({
        message: `Syncable ${this.handle} is now out of backoff mode.`,
        tag: 'syncable',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'INFO',
      })
    }
    this.currentUnsuccessfulExecutions = 0
  }

  /**
   * Marks the syncable as unsuccessfully executed.
   * Adds Backoff logic according to the syncable to not retry the execution immediately
   */
  markUnsuccessfulExecution() {
    this.currentUnsuccessfulExecutions++
    if (this.currentUnsuccessfulExecutions >= this.numberOfUnsuccessfulExecutionsUntilBackoff) {
      useLogsStore().addLogEntry({
        message: `Syncable ${this.handle} is now in backoff mode. It will not execute for the next
        ${this.backoffTime / 1000} seconds.`,
        tag: 'syncable',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'WARN',
      })
    }
  }

  /**
   * Attaches Watchers to syncable preconditions.
   */
  attachWatchers() {
    watch(
      this.satisfiesPreconditions,
      () => {
        this.maybeExecute().catch(e => {
          useLogsStore().addLogEntry({
            message: `Syncable ${this.handle} failed to execute`,
            error: e,
            tag: 'syncable',
            globalUniqueIdentifier: this.globalUniqueIdentifier,
            level: 'ERROR',
          })
        })
      },
      {
        immediate: true,
        deep: true,
        flush: 'post',
      }
    )
  }

  /**
   * Checks whether the syncable should be executed.
   * If the Preconditions are not met, it will not execute.
   * If Preconditions are Met and No Initial Data is available, it will execute.
   * If Preconditions are Met and Initial Data is available, it will check the last execution time.
   * If the last execution time is older than the execution interval, it will execute.
   * Otherwise, it will not execute.
   * @param {boolean} failIfPreconditionsNotMet - If true, the method will reject if preconditions are not met.
   * @returns {Promise<void|*>}
   */
  async maybeExecute(failIfPreconditionsNotMet = false) {
    if (this.currentUnsuccessfulExecutions >= this.numberOfUnsuccessfulExecutionsUntilBackoff && this.lastExecuted > Date.now() - this.backoffTime) {
      //Syncable currently in backoff mode, do not execute
      return Promise.resolve()
    }
    if (!this.satisfiesPreconditions.value) {
      //If the preconditions are not met, the syncable does not need to be executed
      if (failIfPreconditionsNotMet) return Promise.reject()
      return this.attachPromiseForNextExecution()
    }
    if (!this.initialDataAvailable.value) {
      return this.execute()
    }
    if (!this.lastExecuted || Date.now() - this.lastExecuted >= this.executionInterval) {
      return this.execute()
    }
    return Promise.resolve()
  }

  /**
   * Executes the syncable workload.
   * Checks if the syncable is currently executing and if so, returns the current promise.
   * If a "Next Execution Promise" is attached, it will resolve it after the workload is done.
   * @returns {Promise<null>}
   */
  async execute() {
    if (this.currentlyExecuting) {
      //The Syncable is already executing, return the current promise
      return this.currentWorkloadPromise
    }
    try {
      //execute the Workload
      useLogsStore().addLogEntry({
        message: `Syncable ${this.handle} is executing`,
        tag: 'syncable',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'INFO',
      })
      this.currentlyExecuting = true
      this.currentWorkloadPromise = this.workload()
        .then(successful => {
          if (successful) {
            this.markSuccessfulExecution()
          } else {
            this.markUnsuccessfulExecution()
          }
          if (this.nextExecutionPromise) this.nextExecutionResolve()
        })
        .catch(e => {
          this.markUnsuccessfulExecution()
          if (this.nextExecutionPromise) this.nextExecutionReject(e)
          throw e
        })
      await this.currentWorkloadPromise
    } catch (e) {
      useLogsStore().addLogEntry({
        message: `Syncable ${this.handle} failed to execute`,
        error: e,
        tag: 'syncable',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'ERROR',
      })
    } finally {
      this.currentlyExecuting = false
      this.lastExecuted = Date.now()
      this.currentWorkloadPromise = null
    }
  }

  /**
   * Attaches a promise for the next execution of the syncable.
   * This is used to ensure that the syncable can be awaited for its next execution.
   * @returns {null}
   */
  attachPromiseForNextExecution() {
    if (!this.nextExecutionPromise) {
      this.nextExecutionPromise = new Promise((resolve, reject) => {
        this.nextExecutionResolve = resolve
        this.nextExecutionReject = reject
      })
      this.nextExecutionPromise.finally(() => {
        this.nextExecutionPromise = null
        this.nextExecutionResolve = null
        this.nextExecutionReject = null
      })
    }
    return this.nextExecutionPromise
  }

  /**
   * The workload that the syncable will execute.
   * This method needs to be implemented by subclasses.
   * @returns {Promise<void>} - A promise that resolves when the workload is complete.
   */
  async workload() {
    throw new Error('workload() not implemented')
  }

  /**
   * Checks if the syncable has initial data available.
   * This method needs to be implemented by subclasses.
   * @returns {boolean} - true if initial data is available, false otherwise.
   */
  hasInitialData() {
    throw new Error('hasInitialData() not implemented')
  }

  /**
   * Returns the handle of the syncable.
   * This method needs to be implemented by subclasses.
   * @return {string} - The handle of the syncable.
   */
  static getSyncableHandle() {
    throw new Error('getSyncableHandle() not implemented')
  }
}

export default Syncable
